import React from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { isEmpty } from 'lodash';

import ListingTypeIcons from '@components/table/icons/ListingTypeIcons';
import StatusBadges from '@components/badges/StatusBadges';
import TableSearchInputGroup from '@components/smartAdminUI/input/TableSearchInputGroup';
import ListingActionButtons from '@components/table/actionButtons/ListingActionButtons';
import Button from '@components/smartAdminUI/Button';
import AgentAvatar from '@components/table/avatar/AgentAvatar';

const ListingsTable = ({
  displayData,
  onListingSelect,
  onCopyClick,
  onAuditClick,
  isModal = false,
}) => {
  const actionsFormatter = (cell, row) => (
    <ListingActionButtons
      viewClicked={onListingSelect}
      copyClicked={onCopyClick}
      auditClicked={onAuditClick}
      currRow={row}
      isCopiable={row?.isCopyable}
      isEditable={row?.isEditable}
      isAuditable={row?.isAuditable}
      sizeSM={false}
    />
  );

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'actions',
      text: ' ',
      formatter: actionsFormatter,
      headerStyle: { width: '175px' },
    },
    {
      dataField: 'UniqueID',
      text: 'Unique ID',
      hidden: true,
    },
    {
      dataField: 'ListingID',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '40px' },
    },
    {
      dataField: 'StandardStatusVerb',
      text: 'Status',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      align: 'center',
      headerStyle: { width: '70px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <StatusBadges status={cellContent} />),
    },
    {
      dataField: 'PropertyType',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '50px' },
      align: 'center',
      formatter: cellContent => (isEmpty(cellContent) ? '' : <ListingTypeIcons type={cellContent} />),
    },
    {
      dataField: 'UnparsedAddress',
      text: 'Address',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-175',
    },
    {
      dataField: 'City',
      text: 'City',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      classes: 'text-ellipsis-150',
    },
    {
      dataField: 'ListAgentFullName',
      text: 'Listing Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '150px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <AgentAvatar fullName={cellContent} showFullName />),
    },
    {
      dataField: 'CoListAgentFullName',
      text: 'Co-List Agent',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '150px' },
      formatter: cellContent => (isEmpty(cellContent) ? '' : <AgentAvatar fullName={cellContent} showFullName />),
    },
  ];

  // Sets the default sort order to desc by ListingId
  const defaultSorted = [{
    dataField: 'ListingId',
    order: 'desc',
  }];

  // Buttons on the bottom left of the table that allow
  // the user to decide on how many rows per page should
  // be shown at one time.
  const sizePerPageRenderer = ({
    options,
    currSizePerPage,
    onSizePerPageChange,
  }) => (
    <div className="btn-group" role="group">
      {
        options.map(option => {
          const isSelect = currSizePerPage === `${option.page}`;
          return (
            <Button
              variant={`${isSelect ? 'primary' : 'outline-primary'}`}
              key={option.text}
              onClick={() => onSizePerPageChange(option.page)}
            >
              {option.text}
            </Button>
          );
        })
      }
    </div>
  );

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      &nbsp;
      {`Showing rows ${from} to ${to} of ${size}`}
    </span>
  );

  const options = {
    firstPageText: '<<',
    hidePageListOnlyOnePage: true,
    lastPageText: '>>',
    nextPageText: '>',
    paginationTotalRenderer: customTotal,
    prePageText: '<',
    showTotal: true,
    sizePerPageList: [],
    sizePerPage: isModal ? 5 : 10,
    sizePerPageRenderer,
  };

  return (
    <ToolkitProvider
      columns={columns}
      data={displayData}
      keyField="UniqueID"
      search
    >
      {toolKit => (
        <>
          {displayData.length > 1 && <TableSearchInputGroup {...toolKit.searchProps} />}
          <BootstrapTable
            wrapperClasses="table-responsive"
            classes="dataTable"
            bordered={false}
            striped
            hover
            id="listingTable"
            noDataIndication="Your search returned no results. Please try adjusting your search criteria."
            pagination={displayData.length > 1 ? paginationFactory(options) : null}
            {...toolKit.baseProps}
            defaultSorted={defaultSorted}
          />
        </>
      )}
    </ToolkitProvider>
  );
};

ListingsTable.propTypes = {
  displayData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onListingSelect: PropTypes.func.isRequired,
  onCopyClick: PropTypes.func.isRequired,
  onAuditClick: PropTypes.func.isRequired,
  isModal: PropTypes.bool,
};

export default ListingsTable;
